/**
 * Business Hours helpers — shared between the editor, public display,
 * and the "Open now / Closed now" badge.
 *
 * Shape (vendor stores under supplier.business_hours):
 * {
 *   mon: { open: true,  from: "09:00", to: "19:00" },
 *   ...
 *   sun: { open: false, from: "",      to: ""      }
 * }
 */

export const DAYS = [
  { key: 'mon', short: 'Mon', long: 'Monday'    },
  { key: 'tue', short: 'Tue', long: 'Tuesday'   },
  { key: 'wed', short: 'Wed', long: 'Wednesday' },
  { key: 'thu', short: 'Thu', long: 'Thursday'  },
  { key: 'fri', short: 'Fri', long: 'Friday'    },
  { key: 'sat', short: 'Sat', long: 'Saturday'  },
  { key: 'sun', short: 'Sun', long: 'Sunday'    },
];

export const DEFAULT_HOURS = DAYS.reduce((acc, d) => {
  acc[d.key] = { open: d.key !== 'sun', from: '09:00', to: '19:00' };
  return acc;
}, {});

const to12h = (hm) => {
  if (!hm || !/^\d{2}:\d{2}$/.test(hm)) return hm || '';
  const [h, m] = hm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${period}` : `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

/**
 * Collapses consecutive days with identical hours into ranges:
 *   "Mon-Fri 9 AM-7 PM, Sat 10 AM-2 PM, Sun Closed"
 * Returns null when the entire week is unset/blank so callers can render
 * the "Contact for hours" placeholder.
 */
export const formatHoursDisplay = (hours) => {
  if (!hours || typeof hours !== 'object') return null;
  const haveAny = DAYS.some((d) => {
    const h = hours[d.key];
    return h && (h.open || h.from || h.to);
  });
  if (!haveAny) return null;

  const segments = DAYS.map((d) => {
    const h = hours[d.key] || { open: false };
    if (!h.open || !h.from || !h.to) return { day: d, label: 'Closed' };
    return { day: d, label: `${to12h(h.from)}-${to12h(h.to)}` };
  });

  const groups = [];
  let curr = null;
  segments.forEach((s) => {
    if (curr && curr.label === s.label) {
      curr.endDay = s.day;
    } else {
      if (curr) groups.push(curr);
      curr = { startDay: s.day, endDay: s.day, label: s.label };
    }
  });
  if (curr) groups.push(curr);

  return groups
    .map((g) => {
      const range = g.startDay.key === g.endDay.key
        ? g.startDay.short
        : `${g.startDay.short}-${g.endDay.short}`;
      return `${range} ${g.label}`;
    })
    .join(', ');
};

/**
 * Returns true / false / null. `null` when no hours configured (treat as unknown).
 */
export const isOpenNow = (hours, now = new Date()) => {
  if (!hours || typeof hours !== 'object') return null;
  const dayKey = DAYS[(now.getDay() + 6) % 7].key; // JS: Sun=0, our DAYS: mon=0
  const h = hours[dayKey];
  if (!h || !h.open || !h.from || !h.to) {
    // If nothing at all configured, return null (unknown) so badge hides.
    const anyOpenDay = DAYS.some((d) => hours[d.key]?.open);
    return anyOpenDay ? false : null;
  }
  const [fh, fm] = h.from.split(':').map(Number);
  const [th, tm] = h.to.split(':').map(Number);
  const minsNow = now.getHours() * 60 + now.getMinutes();
  const minsFrom = fh * 60 + fm;
  const minsTo = th * 60 + tm;
  // Support overnight (e.g. 22:00 -> 02:00 next day)
  if (minsTo <= minsFrom) return minsNow >= minsFrom || minsNow < minsTo;
  return minsNow >= minsFrom && minsNow < minsTo;
};
